import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DiscordIcon, DocIcon, TelegramIcon } from 'src/assets/icons';
import { PATHS } from 'src/constants/paths';
import useWindowSize from 'src/hooks/useWindowSize';
import { useVerifyToken } from 'src/services/auth/verify';
import { useAppSelector } from 'src/store';
import MenuItem from './MenuItem';
import './styles.scss';
import { useAuth } from 'src/libs/rainbow';
import { useAppTranslation } from 'src/i18n/hooks';
import { IUserInfo } from 'src/store/reducers/user';
import { returnRawValue, useLocalStorage } from 'src/hooks/useLocalStorage';
import { desktopAddressKey } from 'src/helpers/storage';
import { Address } from 'viem/accounts';
import { useGetCompetitionEligibility } from 'src/services/events/get-competition-eligibility';

export interface SubMenu {
  label: string;
  path?: string;
  icon?: string;
  link?: string;
}
export interface IMenuItem {
  label: string;
  path?: string;
  link?: string;
  subMenus?: SubMenu[];
  id?: string;
}

const SubMenu: React.FC = () => {
  const { pathname } = useLocation();
  const currentPair = useAppSelector((state) => state.pairs.currentPair);
  const verifyTokenQuery = useVerifyToken() as { data?: { role?: string } };
  const { width } = useWindowSize();
  const { isAuthenticated } = useAuth();
  const { t } = useAppTranslation('navbar');
  const userInfo: IUserInfo = useAppSelector((state) => state.user);
  const [linkedDeskTopAddress] = useLocalStorage(desktopAddressKey, undefined, {
    serialize: returnRawValue,
    deserialize: returnRawValue,
  });

  const walletAddress = (linkedDeskTopAddress as Address) ?? userInfo.walletAddress;

  const moreMenu = useMemo(() => {
    return [
      {
        label: t('More'),
        path: 'More',
        id: 'more-menu',
        subMenus: [
          {
            label: t('Announcements'),
            path: PATHS.announcements(),
          },
          {
            label: t('Discord'),
            icon: DiscordIcon,
            link: 'https://discord.com/invite/vdex-trade',
          },
          {
            label: t('Telegram'),
            icon: TelegramIcon,
            link: 'https://t.me/VDEX_Community',
          },
          {
            label: t('Docs'),
            link: 'https://docs.vdex.trade/',
            icon: DocIcon,
          },
        ],
      },
    ];
  }, [width, isAuthenticated, t]);

  const renderListMenu = () => {
    const listMenu: IMenuItem[] = [
      {
        label: t('Trade'),
        path: `${PATHS.trade()}/${currentPair?.baseToken}`,
      },
      {
        label: 'Swap',
        path: PATHS.swap(),
      },
      {
        label: t('VMM'),
        path: PATHS.liquidityPool(),
      },
      {
        label: t('Portfolio'),
        path: PATHS.portfolio(),
      },
      {
        label: t('Referrals'),
        path: PATHS.referrals(),
      },
      {
        label: t('leaderboard', {
          ns: 'leaderboard',
        }),
        path: PATHS.tradingLeaderboard(),
      },
      ...(isAuthenticated
        ? [
            {
              label: t('Competition'),
              path: PATHS.tradingCompetition(),
            },
          ]
        : []),

      ...(walletAddress && verifyTokenQuery.data?.role === 'ADMIN'
        ? [
            {
              label: t('Admin'),
              path: PATHS.admin(),
            },
          ]
        : []),

      ...moreMenu,
    ];

    return listMenu.map((itemMap: IMenuItem, i: number) => {
      return (
        <div id={itemMap.id} className={`relative ${itemMap.label.toLowerCase()}-menu`} key={i}>
          {itemMap.label === t('leaderboard', { ns: 'leaderboard' }) && (
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold shadow">
              {t('New')}
            </span>
          )}
          <MenuItem
            key={i}
            id={itemMap.id}
            label={itemMap.label}
            path={itemMap.path}
            subMenus={itemMap.subMenus}
            link={itemMap.link}
          />
          {itemMap.path?.includes(pathname) && (
            <div className="h-0.5 w-full absolute -bottom-5 bg-[#3366FF]"></div>
          )}
        </div>
      );
    });
  };

  return <div className="flex gap-4  2xl:gap-8 h-[100%]">{renderListMenu()}</div>;
};

export default React.memo(SubMenu);
